import type { PlantConfig } from '../../engine/plant/types';
import { equipment, extraTags, streams } from './equipment';
import { instruments } from './instruments';
import { alarms, interlocks, loops, onOffLoops, sequences } from './control';
import { faults, kpis } from './faults';
import { layout } from './layout';
import { scenarios, tuningChallenges } from './scenarios';
import { designPreset } from './preset';
import { lessons } from './lessons';

/**
 * Municipal water treatment plant, 12 MLD (500 m³/h).
 * Design basis and expected steady state: docs/PROCESS_MODELS.md §5.1.
 */
export const waterTreatmentPlant: PlantConfig = {
  id: 'water-treatment',
  name: 'Riverside Water Treatment Works',
  description:
    'A 12 megalitre-per-day conventional surface water plant: river intake, alum ' +
    'coagulation, flocculation, sedimentation, four rapid gravity filters, chlorine ' +
    'contact, clearwell and high-lift distribution pumping.',
  species: ['ntu', 'floc', 'alum', 'cl2', 'alk'],
  density: 1000,
  designConditions: {
    flow: 500,
    rawTurbidity: 8,
    rawTemperature: 285.15,
    alumDose: 15.1,
    settledTurbidity: 1.5,
    filteredTurbidity: 0.05,
    chlorineResidual: 1.0,
    ctRatio: 1.21,
    ph: 7.8,
    clearwellLevel: 65,
    distributionPressure: 4.5,
  },
  equipment,
  streams,
  instruments,
  loops,
  onOffLoops,
  sequences,
  alarms,
  interlocks,
  faults,
  kpis,
  layout,
  scenarios: [...scenarios, ...tuningChallenges],
  lessons,
  extraTags,
  designPreset,
  // Three hours of settling produce the design state. That run is done once, offline, by
  // tools/gen-steady-state; the result is checked in and restored instantly at startup.
  warmStartS: 10_800,
  trendGroups: [
    { id: 'overview', name: 'Plant overview', tags: ['FT-101.PV', 'LT-501.PV', 'AT-405.PV', 'AT-601.PV', 'PT-701.PV'] },
    { id: 'coagulation', name: 'Coagulation', tags: ['AT-101.PV', 'MX-201.DOSE', 'MX-201.DOSE_OPT', 'AT-301.PV', 'AIC-201.OP'] },
    { id: 'filters', name: 'Filters', tags: ['FT-401.PV', 'FT-402.PV', 'FT-403.PV', 'FT-404.PV', 'PDT-401.PV', 'PDT-402.PV', 'PDT-403.PV', 'PDT-404.PV'] },
    { id: 'filter-quality', name: 'Filter quality', tags: ['AT-401.PV', 'AT-402.PV', 'AT-403.PV', 'AT-404.PV', 'AT-405.PV'] },
    { id: 'disinfection', name: 'Disinfection', tags: ['AT-601.PV', 'CT-601.CTRATIO', 'PD-601.LPH', 'AT-602.PV'] },
    { id: 'distribution', name: 'Distribution', tags: ['LT-501.PV', 'FT-701.PV', 'PT-701.PV', 'SPD-701'] },
  ],
};
